import { useState } from "react";

import useAxios from "@hooks/use-axios";
import { toastify } from "@helpers/toast";
import { OBJECT_URL_TTL_MS } from "../misc/constants";
import { documentErrorOf, isSessionError, pdfFileName, saveObjectUrl } from "../misc/helpers";

/**
 * Opens or downloads a contract through the API's /document routes, which is
 * the only way any screen gets at the PDF. Until every party has signed, the
 * stamped document is built on request and stored nowhere, so `contract_url`
 * is the raw template — it must never be linked to directly.
 *
 * The routes need the bearer token, so the bytes are fetched as a blob and
 * handed to the browser as an object URL.
 *
 * `pendingPath` is the path being fetched, so a table can put the spinner on
 * the one row that was clicked.
 * @param base the useAxios base for the role: ops | brand | affiliate
 */
const useContractDocument = (base) => {
    const { axiosInstance } = useAxios({ base, showAlert: false });
    const [pendingPath, setPendingPath] = useState("");

    const fetchUrl = async (path) => {
        const blob = await axiosInstance.get(path, { responseType: "blob" });
        return URL.createObjectURL(blob);
    };

    const fail = async (error) => {
        if (isSessionError(error)) return;
        toastify({ title: await documentErrorOf(error), type: "error" });
    };

    // The tab is opened before the fetch, while the click still counts as a
    // user gesture — a window opened after an await is a popup, and blocked.
    // If it was blocked anyway, the document is saved instead.
    const openDocument = async (path, name = "") => {
        if (!path) return;

        const tab = window.open("", "_blank");
        setPendingPath(path);

        try {
            const url = await fetchUrl(path);

            if (tab) {
                tab.location.replace(url);
                window.setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_TTL_MS);
            } else {
                saveObjectUrl(url, pdfFileName(name));
            }
        } catch (error) {
            tab?.close();
            await fail(error);
        } finally {
            setPendingPath("");
        }
    };

    const downloadDocument = async (path, name = "") => {
        if (!path) return;

        setPendingPath(path);

        try {
            saveObjectUrl(await fetchUrl(path), pdfFileName(name));
        } catch (error) {
            await fail(error);
        } finally {
            setPendingPath("");
        }
    };

    return { pendingPath, openDocument, downloadDocument };
};

export default useContractDocument;
